/**
 * Axios response interceptor — transparent JWT refresh on 401.
 *
 * When the server returns a 401 Unauthorized response the interceptor:
 * 1. Attempts to refresh the access token via `refreshAccessToken()`.
 * 2. Replays the original request with the new token.
 * 3. Queues any concurrent requests that arrive during the refresh and
 *    replays them all once the new token is available.
 *
 * If the refresh itself fails (e.g. the refresh token has also expired)
 * the error is propagated; the saga/component layer is responsible for
 * redirecting the user to login.
 */
import axios from "./request";
import { getAccessToken } from "@service/local-storage-handler";
import { refreshAccessToken } from "@service/user-handler";

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (accessToken) => {
  refreshSubscribers.forEach((callback) => callback(accessToken));
};

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || "";
    const isAuthEndpoint =
      requestUrl.includes("auth/session") ||
      requestUrl.includes("oauth/token") ||
      requestUrl.includes("oauth/logout") ||
      requestUrl.includes("/logout");

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      // Mark request as retried so a second 401 on the replayed request
      // does not start another refresh cycle indefinitely.
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          await refreshAccessToken();
          const accessToken = getAccessToken();
          originalRequest.headers["Authorization"] = "Bearer " + accessToken;
          onTokenRefreshed(accessToken);
          refreshSubscribers = [];
          isRefreshing = false;
          return axios(originalRequest);
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          refreshSubscribers = [];
          isRefreshing = false;
          return Promise.reject(refreshError);
        }
      } else {
        return new Promise((resolve) => {
          subscribeTokenRefresh((accessToken) => {
            originalRequest.headers["Authorization"] = "Bearer " + accessToken;
            resolve(axios(originalRequest));
          });
        });
      }
    }
    return Promise.reject(error);
  }
);

export default axios;
